import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Camera, 
  ArrowLeft, 
  Crown, 
  Check, 
  X, 
  Users, 
  Calendar, 
  Image, 
  BarChart3,
  Sparkles,
  Send,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import SEO from "../components/SEO";

const StudioContactPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentSystems, setCurrentSystems] = useState([]);
  const [systemInput, setSystemInput] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    team_size: "",
    event_types: "",
    monthly_sessions: "",
    additional_info: ""
  });

  const studioFeatures = [
    { name: "Everything in Pro", included: true },
    { name: "Multi-photographer teams", coming_soon: true },
    { name: "Shared team calendars", coming_soon: true },
    { name: "Team galleries", coming_soon: true },
    { name: "Advanced client portal", coming_soon: true },
    { name: "Analytics & reporting", coming_soon: true },
    { name: "Custom branding", included: true },
    { name: "Priority support", included: true },
    { name: "White-glove onboarding", included: true }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSystem = (e) => {
    if (e.key === "Enter" && systemInput.trim()) {
      e.preventDefault();
      if (!currentSystems.includes(systemInput.trim())) {
        setCurrentSystems(prev => [...prev, systemInput.trim()]);
      }
      setSystemInput("");
    }
  };

  const handleRemoveSystem = (system) => {
    setCurrentSystems(prev => prev.filter(s => s !== system));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      toast.error("Please fill in your name and email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/studio-inquiry`, {
        ...formData,
        current_systems: currentSystems
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit inquiry");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-12 px-6" data-testid="studio-contact-success">
        <SEO 
          title="Thank You | 360Lumina Studio"
          description="Thank you for your interest in 360Lumina Studio"
        />
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-4xl font-serif mb-4">Thank You!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              We have received your inquiry and are excited to learn more about your photography business.
              A member of our team will be in touch within 1-2 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/features">
                <Button variant="outline">Explore Features</Button>
              </Link>
              <Link to="/">
                <Button>Back to Home</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" data-testid="studio-contact-page">
      <SEO 
        title="Studio Early Access | 360Lumina Photography Platform"
        description="Contact us about 360Lumina Studio - built for growing photography teams with multi-photographer support, team calendars, and advanced analytics."
      />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <Link to="/pricing" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Pricing
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <Camera className="w-8 h-8 text-primary" />
          <span className="text-2xl font-serif">360Lumina</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Studio Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                Early Access
              </Badge>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-serif mb-4">
              Studio
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              Built for growing photography teams
            </p>

            <Card className="bg-card border-border mb-8">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">What is Included</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {studioFeatures.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-400 shrink-0" />
                      <span className={feature.coming_soon ? "text-muted-foreground" : ""}>
                        {feature.name}
                      </span>
                      {feature.coming_soon && (
                        <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-400 border-yellow-500/30">
                          Coming Soon
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <div className="p-6 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-primary mt-1" />
                <div>
                  <p className="font-medium mb-1">Why Early Access?</p>
                  <p className="text-sm text-muted-foreground">
                    Studio plans are currently offered with guided setup so we can tailor the experience to your team. 
                    You will get personalized onboarding and direct access to our team as we build out the features you need.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>Talk to Us</CardTitle>
                <CardDescription>
                  Tell us about your studio and we will be in touch to discuss how 360Lumina can help your team grow.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        data-testid="studio-contact-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        data-testid="studio-contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={handleInputChange}
                        data-testid="studio-contact-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_name">Company / Studio Name</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        placeholder="Your studio name"
                        value={formData.company_name}
                        onChange={handleInputChange}
                        data-testid="studio-contact-company"
                      />
                    </div>
                  </div>

                  {/* Studio Details */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="team_size">Team Size</Label>
                      <Select
                        value={formData.team_size}
                        onValueChange={(value) => handleSelectChange("team_size", value)}
                      >
                        <SelectTrigger data-testid="studio-contact-team-size">
                          <SelectValue placeholder="Select team size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="2-3">2-3 photographers</SelectItem>
                          <SelectItem value="4-6">4-6 photographers</SelectItem>
                          <SelectItem value="7-10">7-10 photographers</SelectItem>
                          <SelectItem value="10+">10+ photographers</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthly_sessions">Monthly Sessions</Label>
                      <Select
                        value={formData.monthly_sessions}
                        onValueChange={(value) => handleSelectChange("monthly_sessions", value)}
                      >
                        <SelectTrigger data-testid="studio-contact-monthly-sessions">
                          <SelectValue placeholder="Avg. sessions per month" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1-10">1-10 sessions</SelectItem>
                          <SelectItem value="11-25">11-25 sessions</SelectItem>
                          <SelectItem value="26-50">26-50 sessions</SelectItem>
                          <SelectItem value="50+">50+ sessions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="event_types">Primary Event Types</Label>
                    <Input
                      id="event_types"
                      name="event_types"
                      placeholder="e.g., Weddings, Corporate Events, Portraits"
                      value={formData.event_types}
                      onChange={handleInputChange}
                      data-testid="studio-contact-event-types"
                    />
                  </div>

                  {/* Current Systems - Tag Input */}
                  <div className="space-y-2">
                    <Label>Current Systems You Are Using</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Press Enter to add each system (e.g., Honeybook, Dubsado, ShootProof)
                    </p>
                    <Input
                      placeholder="Type a system name and press Enter"
                      value={systemInput}
                      onChange={(e) => setSystemInput(e.target.value)}
                      onKeyDown={handleAddSystem}
                      data-testid="studio-contact-systems-input"
                    />
                    {currentSystems.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {currentSystems.map((system, i) => (
                          <Badge
                            key={i}
                            variant="secondary"
                            className="px-3 py-1 flex items-center gap-2"
                          >
                            {system}
                            <button
                              type="button"
                              onClick={() => handleRemoveSystem(system)}
                              className="hover:text-red-400"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-2">
                    <Label htmlFor="additional_info">Additional Questions or Details</Label>
                    <Textarea
                      id="additional_info"
                      name="additional_info"
                      placeholder="Tell us more about your needs, questions, or anything else you'd like us to know..."
                      value={formData.additional_info}
                      onChange={handleInputChange}
                      rows={4}
                      data-testid="studio-contact-additional-info"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="studio-contact-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Inquiry
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default StudioContactPage;
